import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/dashboard/PageHeader';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, RefreshCw } from 'lucide-react';
import InvoicesStatsBar from '@/components/invoices/InvoicesStatsBar';
import InvoicesTable from '@/components/invoices/InvoicesTable';
import { useResolvedImageUrl } from '@/components/estimates/ProposalImage';
import {
  fetchJobsForInvoices,
  flattenInvoices,
  computeInvoiceStats,
  applyInvoiceFilter,
  markInvoicePaidById,
  markInvoiceUnpaidById,
} from '@/lib/invoicesData';
import { toast } from 'sonner';
import { fetchSettingsByKey, SETTINGS_KEYS } from '@/lib/settingsData';

export default function InvoicesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [company, setCompany] = useState(null);
  const logoSrc = useResolvedImageUrl({ url: company?.logo_url, path: company?.logo_path });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('due_soonest');
  const [busyRowId, setBusyRowId] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const [data, companyRes] = await Promise.all([
        fetchJobsForInvoices(),
        fetchSettingsByKey(SETTINGS_KEYS.COMPANY, {}),
      ]);
      setJobs(data);
      setCompany(companyRes?.value || null);
    } catch (e) {
      setError(e?.message || t('invoices.errors.load'));
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const allRows = useMemo(() => flattenInvoices(jobs), [jobs]);
  const stats = useMemo(() => computeInvoiceStats(allRows), [allRows]);
  const filteredRows = useMemo(
    () => applyInvoiceFilter(allRows, { search, status: statusFilter, sort }),
    [allRows, search, statusFilter, sort]
  );

  const findJob = (jobId) => jobs.find((j) => j.id === jobId);

  const handleOpenJob = (row) => {
    if (!row?.job_id) return;
    navigate(`/dashboard/active-jobs?jobId=${encodeURIComponent(row.job_id)}`);
  };

  const handleMarkPaid = async (row) => {
    const job = findJob(row.job_id);
    if (!job) return;
    setBusyRowId(row.row_key);
    try {
      const updatedJob = await markInvoicePaidById(job, row.invoice_id);
      setJobs((cur) =>
        cur.map((j) => (j.id === updatedJob.id ? { ...j, ...updatedJob } : j))
      );
      toast.success(t('invoices.toast_paid'));
    } catch (e) {
      toast.error(e?.message || t('invoices.toast_mark_failed'));
    } finally {
      setBusyRowId(null);
    }
  };

  const handleMarkUnpaid = async (row) => {
    const job = findJob(row.job_id);
    if (!job) return;
    setBusyRowId(row.row_key);
    try {
      const updatedJob = await markInvoiceUnpaidById(job, row.invoice_id);
      setJobs((cur) =>
        cur.map((j) => (j.id === updatedJob.id ? { ...j, ...updatedJob } : j))
      );
      toast.success(t('invoices.toast_unpaid'));
    } catch (e) {
      toast.error(e?.message || t('invoices.toast_unmark_failed'));
    } finally {
      setBusyRowId(null);
    }
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader
        eyebrow={t('invoices.eyebrow')}
        title={t('invoices.title')}
        subtitle={t('invoices.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => load({ silent: true })}
            disabled={loading || refreshing}
            className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
            data-testid="invoices-refresh"
          >
            <RefreshCw
              size={14}
              className={`mr-2 ${refreshing ? 'animate-spin' : ''}`}
            />
            {t('common.refresh')}
          </Button>
        }
      />

      {error ? (
        <Alert
          className="mb-5 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="invoices-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      {company?.logo_url ? (
        <div
          className="mb-4 inline-flex items-center gap-3 rounded-md border border-[hsl(var(--hairline))] bg-[hsl(var(--card))] pl-2 pr-4 py-2"
          data-testid="invoices-company-logo-banner"
        >
          <img
            src={logoSrc}
            alt={`${company.name || 'Company'} logo`}
            className="h-8 w-auto max-w-[120px] object-contain rounded-sm bg-[#0f0e0c] p-0.5"
          />
          <div className="text-xs">
            {company.name ? (
              <div className="text-foreground font-medium">{company.name}</div>
            ) : null}
            <div className="text-muted-foreground text-[10px] uppercase tracking-[0.18em]">
              {t('invoices.invoice_header_badge')}
            </div>
          </div>
        </div>
      ) : null}

      <InvoicesStatsBar stats={stats} />

      <InvoicesTable
        rows={filteredRows}
        loading={loading}
        statusFilter={statusFilter}
        onStatusFilter={setStatusFilter}
        search={search}
        onSearch={setSearch}
        sort={sort}
        onSort={setSort}
        counts={stats.counts}
        onOpenJob={handleOpenJob}
        onMarkPaid={handleMarkPaid}
        onMarkUnpaid={handleMarkUnpaid}
        busyRowId={busyRowId}
      />
    </div>
  );
}
